// Picks readable colours for UI that floats over the page — the header's menu
// label and hamburger, which pass over white, brand black, zinc, and the green
// mesh gradients as they scroll.
//
// A blend mode would be the tidier answer, but `mix-blend-mode` only blends
// within the nearest stacking context, and the header has to be `z-50` to clear
// the navigation overlay — so it would blend against the header's own (empty)
// group and stay one flat colour. Sampling the backdrop instead keeps the
// layering intact.
//
// Sections are read rather than tagged: a section that forgot a tag would ship
// as unreadable text instead of as a build error. `data-surface` takes a CSS
// colour, or `var(--token)`, and exists only for backdrops that can't be read —
// images and gradients, which declare the colour the header crosses.

const BRAND_BLACK = [30, 30, 31]; // --color-brand-black
const PRIMARY = [93, 214, 0]; // --color-primary

// Below this the brand green is too close to its backdrop to make out. It sits
// well clear of both sides: another green measures 0.15 or less, a yellow 0.20,
// while the nearest non-green backdrop on the site (gray-100) is 0.31.
const MIN_GREEN_DISTANCE = 0.25;

const channel = (value) => {
    const c = value / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
};

// WCAG 2.1 relative luminance and contrast ratio.
const luminance = ([r, g, b]) => 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
const contrast = (a, b) => (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);

const BRAND_BLACK_LUMINANCE = luminance(BRAND_BLACK);
const WHITE_LUMINANCE = 1;

// Perceptual distance in OKLab. Luminance alone can't answer "is this green
// lost against that green": two colours a contrast ratio calls distinguishable
// still disappear into each other when they share a hue.
const oklab = ([r, g, b]) => {
    const R = channel(r);
    const G = channel(g);
    const B = channel(b);

    const l = Math.cbrt(0.4122214708 * R + 0.5363325363 * G + 0.0514459929 * B);
    const m = Math.cbrt(0.2119034982 * R + 0.6806995451 * G + 0.1073969566 * B);
    const s = Math.cbrt(0.0883024619 * R + 0.2817188376 * G + 0.6299787005 * B);

    return [
        0.2104542553 * l + 0.7936177850 * m - 0.0040720468 * s,
        1.9779984951 * l - 2.4285922050 * m + 0.4505937099 * s,
        0.0259040371 * l + 0.7827717662 * m - 0.8086757660 * s,
    ];
};

const perceptualDistance = (a, b) => {
    const [l1, a1, b1] = oklab(a);
    const [l2, a2, b2] = oklab(b);
    return Math.hypot(l1 - l2, a1 - a2, b1 - b2);
};

// Computed colours come back in whatever space they were authored in —
// Tailwind's palette serialises as oklch(), the brand tokens as rgb() — so the
// canvas does the conversion rather than a regex that would read an oklch
// lightness as a red channel and call a pale grey nearly black.
let painter = null;
const cache = new Map();

const toRgba = (value) => {
    if (cache.has(value)) return cache.get(value);

    painter ??= document.createElement('canvas').getContext('2d', { willReadFrequently: true });
    painter.clearRect(0, 0, 1, 1);
    painter.fillStyle = value;
    painter.fillRect(0, 0, 1, 1);

    const rgba = [...painter.getImageData(0, 0, 1, 1).data];
    cache.set(value, rgba);
    return rgba;
};

// Anything see-through leaves whatever is behind it showing, so it isn't the
// backdrop — keep walking back through the stack instead of measuring it.
const parseOpaque = (value) => {
    const [r, g, b, alpha] = toRgba(value);
    return alpha < 230 ? null : [r, g, b];
};

// A gradient whose stops are brand tokens can name one instead of copying its
// value out, which would then quietly go stale if the token were retuned.
const CUSTOM_PROPERTY = /^var\((--[\w-]+)\)$/;

const resolveDeclared = (value, element) => {
    const token = value.match(CUSTOM_PROPERTY);
    return token ? getComputedStyle(element).getPropertyValue(token[1]).trim() : value;
};

/**
 * Reads the colour of the backdrop under an element's centre, or null when
 * nothing opaque sits behind it.
 */
export const sampleSurface = (element) => {
    if (!element) return null;

    const rect = element.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;

    const x = rect.left + rect.width / 2;
    const y = rect.top + rect.height / 2;

    // Front-to-back, and it includes ancestors — so the first opaque background
    // found is the one actually showing. `pointer-events: none` layers are
    // skipped by hit testing, which is what keeps the dormant nav overlay out.
    for (const candidate of document.elementsFromPoint(x, y)) {
        if (candidate.closest('header')) continue;

        const declared = candidate.dataset.surface;
        const rgb = parseOpaque(declared
            ? resolveDeclared(declared, candidate)
            : getComputedStyle(candidate).backgroundColor);
        if (rgb) return rgb;
    }

    return null;
};

/**
 * Ink for text over a sampled surface. Brand black beats white on mid-tones
 * like `bg-primary` and `bg-secondary`, so the two are compared rather than
 * thresholded; an unreadable surface falls back to the white page body.
 */
export const pickInk = (surface) => {
    if (!surface) return 'black';

    const level = luminance(surface);
    return contrast(level, WHITE_LUMINANCE) > contrast(level, BRAND_BLACK_LUMINANCE)
        ? 'white'
        : 'black';
};

/**
 * The hamburger keeps its brand green everywhere except over the greens (and
 * yellows) it would sink into, where white separates it from the backdrop.
 */
export const pickIconInk = (surface) => {
    if (!surface) return 'primary';

    return perceptualDistance(PRIMARY, surface) < MIN_GREEN_DISTANCE ? 'white' : 'primary';
};
